import type { InvokeDefinition, StreamDefinition } from '../../signalr/types'
import { isPending } from '../../containers/TradingList/helpers'
import { createOrderFromResponse } from '../order/helpers'
import type { OrderResponse } from '../order/types'
import { fetchListTradingSecurities } from '../securities/actions'
import { fetchWatchlistDetails } from '../watchList/actions'
import {
  type AddListTradingRowsAction,
  type CreateListTradingListAction,
  type DeleteListTradingSecurityAction,
  type UpdateListTradingListAction,
  type UnwatchOrderAction,
  type WatchOrderAction,
  createListTradingListFailure,
  listTradingSecuritiesFetch,
  removeCancelledOrder,
  setListTradingTransactionId,
  setWatchedOrder,
  unwatchOrder,
  updateListTradingListSuccess
} from './actions'
import type { ListTradingOrder } from './types'

export const createListTradingListDefinition: InvokeDefinition<
  [string, Array<Omit<ListTradingOrder, 'securityId'>>],
  number,
  CreateListTradingListAction
> = {
  method: 'CreateListTradingList',
  getParams: (_state, action) => [
    action.payload.name,
    action.payload.orders.map(({ securityId: _securityId, ...order }) => ({
      ...order,
      cusipOrIsin: order.cusipOrIsin.toUpperCase()
    }))
  ],
  mapResponse: setListTradingTransactionId,
  mapError: (err) => createListTradingListFailure(err.message ?? '')
}

export const updateListTradingListDefinition: InvokeDefinition<
  [number, Array<Record<string, unknown>>],
  unknown,
  UpdateListTradingListAction
> = {
  method: 'UpdateListTradingList',
  getParams: (_state, action) => {
    const orders = action.payload.orders.map((originalOrder) => {
      const {
        securityId: _securityId,
        isTemp: _isTemp,
        selectedOrders: _selectedOrders,
        ordersToCancel: _ordersToCancel,
        ...order
      } = originalOrder
      return order
    })
    return [action.payload.listId, orders]
  },
  mapResponse: (_response, action) =>
    updateListTradingListSuccess(action.payload.orders)
}

/*
   Watch one accumulated order (one GetOrderById stream per id). A live
   re-resolve stores its current size/price; a non-pending one (maker cancelled
   / left the book) removes the id and tears its own stream down. Cancelled is
   recognized via isPending, as deselectCancelledOrders does for own orders.
*/
export const watchOrderDefinition: StreamDefinition<
  [string],
  OrderResponse,
  WatchOrderAction
> = {
  method: 'GetOrderById',
  strategy: 'merge',
  getParams: (_state, action) => [action.payload.orderId],
  mapResponse: (orderResponse, action) => {
    const order = createOrderFromResponse(orderResponse)
    if (isPending(order)) {
      return setWatchedOrder(order)
    }
    return [
      removeCancelledOrder(action.payload.securityId, action.payload.orderId),
      unwatchOrder(action.payload.orderId)
    ]
  },
  cancelOn: ['listTrading.unwatchOrder'],
  cancelFilter: (cancelAction: UnwatchOrderAction, triggerAction) =>
    cancelAction.payload.orderId === triggerAction.payload.orderId
}

export const addNewListTradingRowsDefinition: InvokeDefinition<
  [number, Array<Omit<ListTradingOrder, 'securityId'>>],
  unknown,
  AddListTradingRowsAction
> = {
  method: 'UpdateListTradingList',
  strategy: 'switch',
  getParams: (_state, action) => [
    action.payload.watchlistId,
    action.payload.rows.map(({ securityId: _securityId, ...row }) => row)
  ],
  mapResponse: (_response, action) =>
    fetchWatchlistDetails(action.payload.watchlistId, -1)
}

export const deleteListTradingSecurityDefinition: InvokeDefinition<
  [number, number],
  unknown,
  DeleteListTradingSecurityAction
> = {
  method: 'DeleteListTradingSecurity',
  getParams: (_state, action) => [
    action.payload.listId,
    action.payload.securityId
  ],
  mapResponse: (_response, action) => [
    listTradingSecuritiesFetch(action.payload.listId),
    fetchListTradingSecurities(action.payload.listId, 0)
  ]
}
